import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { getWorkspaceAPIKeys, setWorkspaceAPIKeys, deleteWorkspaceAPIKey } from '../../services/api';
import { useModal } from '../../contexts/ModalContext';

// Icons matching ModelSelector style
const AnthropicIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M14.5 2L22 22h-4L14 14 10 22H6l7.5-20h1z" fill="currentColor"/>
    <path d="M9.5 2L2 22h4l4-8 4 8h4L10.5 2h-1z" fill="currentColor" opacity="0.6"/>
  </svg>
);
AnthropicIcon.propTypes = { size: PropTypes.number };

const OpenAIIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M22.282 9.821a5.985 5.985 0 00-.516-4.91 6.046 6.046 0 00-6.51-2.9A6.065 6.065 0 004.981 4.18a5.985 5.985 0 00-3.998 2.9 6.046 6.046 0 00.743 7.097 5.98 5.98 0 00.51 4.911 6.051 6.051 0 006.515 2.9A5.985 5.985 0 0013.26 24a6.056 6.056 0 005.772-4.206 5.99 5.99 0 003.997-2.9 6.056 6.056 0 00-.747-7.073zM13.26 22.43a4.476 4.476 0 01-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 00.392-.681v-6.737l2.02 1.168a.071.071 0 01.038.052v5.583a4.504 4.504 0 01-4.494 4.494zM3.6 18.304a4.47 4.47 0 01-.535-3.014l.142.085 4.783 2.759a.771.771 0 00.78 0l5.843-3.369v2.332a.08.08 0 01-.033.062L9.74 19.95a4.5 4.5 0 01-6.14-1.646zM2.34 7.896a4.485 4.485 0 012.366-1.973V11.6a.766.766 0 00.388.676l5.815 3.355-2.02 1.168a.076.076 0 01-.071 0l-4.83-2.786A4.504 4.504 0 012.34 7.896zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 01.071 0l4.83 2.791a4.494 4.494 0 01-.676 8.105v-5.678a.79.79 0 00-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 00-.785 0L9.409 9.23V6.897a.066.066 0 01.028-.061l4.83-2.787a4.5 4.5 0 016.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 01-.038-.057V6.075a4.5 4.5 0 017.375-3.453l-.142.08L8.704 5.46a.795.795 0 00-.393.681v6.733zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5v-3z" fill="currentColor"/>
  </svg>
);
OpenAIIcon.propTypes = { size: PropTypes.number };

const GroqIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="4" y="4" width="7" height="7" rx="1" fill="currentColor"/>
    <rect x="13" y="4" width="7" height="7" rx="1" fill="currentColor" opacity="0.7"/>
    <rect x="4" y="13" width="7" height="7" rx="1" fill="currentColor" opacity="0.7"/>
    <rect x="13" y="13" width="7" height="7" rx="1" fill="currentColor" opacity="0.5"/>
  </svg>
);
GroqIcon.propTypes = { size: PropTypes.number };

const KeyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
  </svg>
);

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    <line x1="10" y1="11" x2="10" y2="17"/>
    <line x1="14" y1="11" x2="14" y2="17"/>
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const ExternalLinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/>
    <line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const ShieldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    <path d="M9 12l2 2 4-4"/>
  </svg>
);

const PROVIDERS = {
  openai: {
    name: 'OpenAI',
    icon: OpenAIIcon,
    url: 'https://platform.openai.com/api-keys'
  },
  anthropic: {
    name: 'Anthropic',
    icon: AnthropicIcon,
    url: 'https://console.anthropic.com/settings/keys'
  },
  groq: {
    name: 'Groq',
    icon: GroqIcon,
    url: 'https://console.groq.com/keys'
  }
};

const ProviderRow = ({ 
  provider, 
  value, 
  maskedValue, 
  isAvailable, 
  onChange, 
  onDelete,
  isLoading,
  isLast
}) => {
  const [showKey, setShowKey] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const { showConfirm } = useModal();
  const config = PROVIDERS[provider];
  const IconComponent = config.icon;

  const handleDelete = async () => {
    const confirmed = await showConfirm({
      title: `Delete ${config.name} API Key`,
      message: `Are you sure you want to delete the ${config.name} API key? This will disable ${config.name} models for this workspace.`,
      confirmText: 'Delete Key',
      variant: 'danger'
    });
    
    if (confirmed) {
      onDelete(provider);
    }
  };

  return (
    <div className={`py-5 px-5 ${!isLast ? 'border-b border-border-secondary' : ''}`}>
      {/* Header Row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          {/* Icon with status indicator */}
          <div className="relative">
            <div 
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-200 ${
                isAvailable 
                  ? 'bg-gradient-to-br from-primary-500/20 to-primary-600/30 border border-primary-500/30 text-primary-500'
                  : 'bg-gray-700/50 border border-gray-600/30 text-text-tertiary'
              }`}
            >
              <IconComponent size={20} />
            </div>
            {/* Status dot */}
            <div 
              className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center border-2 border-surface-primary ${
                isAvailable ? 'bg-primary-500 shadow-glow-sm' : 'bg-gray-600'
              }`}
            >
              {isAvailable && <CheckIcon />}
            </div>
          </div>

          {/* Provider info */}
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-text-primary">
              {config.name}
            </h4>
            {isAvailable && (
              <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider rounded bg-success-50 text-primary-500 border border-primary-500/30">
                Active
              </span>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1">
          {isAvailable && !isEditing && (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="p-2 rounded-lg transition-all duration-200 text-text-tertiary hover:bg-success-50 hover:text-primary-500"
                title="Update key"
              >
                <EditIcon />
              </button>
              <button
                onClick={handleDelete}
                disabled={isLoading}
                className="p-2 rounded-lg transition-all duration-200 disabled:opacity-50 text-text-tertiary hover:bg-error-50 hover:text-error"
                title="Delete key"
              >
                <TrashIcon />
              </button>
            </>
          )}
          <a
            href={config.url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-medium text-text-tertiary hover:bg-success-50 hover:text-primary-500"
            title="Get API key"
          >
            <ExternalLinkIcon />
          </a>
        </div>
      </div>

      {/* Input or Display Section */}
      {isEditing || !isAvailable ? (
        <div className="space-y-2">
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary">
              <KeyIcon />
            </div>
            <input
              type={showKey ? 'text' : 'password'}
              value={value}
              onChange={(e) => onChange(provider, e.target.value)}
              placeholder={`Enter your ${config.name} API key...`}
              className="w-full py-2.5 pl-10 pr-10 text-sm rounded-lg font-mono tracking-wide transition-all duration-200 focus:outline-none bg-surface-primary/80 border border-primary-500/20 text-text-primary focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 placeholder:text-text-muted"
              disabled={isLoading}
              autoFocus={isEditing}
            />
            <button
              type="button"
              onClick={() => setShowKey(!showKey)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded transition-colors text-text-tertiary hover:text-text-secondary"
            >
              {showKey ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
          {isAvailable && isEditing && (
            <button
              onClick={() => {
                setIsEditing(false);
                onChange(provider, '');
              }}
              className="text-xs px-3 py-1.5 rounded-lg transition-all duration-200 text-text-tertiary hover:bg-success-50 hover:text-text-secondary"
            >
              Cancel
            </button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-surface-primary/60 border border-primary-500/15">
          <span className="text-primary-500"><KeyIcon /></span>
          <code className="text-sm font-mono tracking-wider flex-1 text-text-secondary">
            {maskedValue}
          </code>
          <span className="text-primary-500" title="Encrypted">
            <ShieldIcon />
          </span>
        </div>
      )}
    </div>
  );
};

ProviderRow.propTypes = {
  provider: PropTypes.string.isRequired,
  value: PropTypes.string,
  maskedValue: PropTypes.string,
  isAvailable: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isLast: PropTypes.bool
};

const APIKeysTab = ({ workspaceId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apiKeysStatus, setApiKeysStatus] = useState({
    is_openai_available: false,
    is_anthropic_available: false,
    is_groq_available: false,
    openai_api_key_masked: null,
    anthropic_api_key_masked: null,
    groq_api_key_masked: null
  });
  const [newKeys, setNewKeys] = useState({
    openai: '',
    anthropic: '',
    groq: ''
  });

  const loadAPIKeys = useCallback(async () => {
    setLoading(true);
    const result = await getWorkspaceAPIKeys(workspaceId);
    if (result.success) {
      setApiKeysStatus(result.data);
    } else {
      toast.error(result.error || 'Failed to load API keys');
    }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => {
    loadAPIKeys();
  }, [loadAPIKeys]);

  const handleKeyChange = (provider, value) => {
    setNewKeys(prev => ({
      ...prev,
      [provider]: value
    }));
  };

  const handleSave = async () => {
    const keysToSave = {};
    if (newKeys.openai) keysToSave.openai_api_key = newKeys.openai;
    if (newKeys.anthropic) keysToSave.anthropic_api_key = newKeys.anthropic;
    if (newKeys.groq) keysToSave.groq_api_key = newKeys.groq;

    if (Object.keys(keysToSave).length === 0) {
      toast.info('No changes to save');
      return;
    }

    setSaving(true);
    const result = await setWorkspaceAPIKeys(workspaceId, keysToSave);
    if (result.success) {
      toast.success('API keys saved successfully');
      setApiKeysStatus(result.data);
      setNewKeys({ openai: '', anthropic: '', groq: '' });
    } else {
      toast.error(result.error || 'Failed to save API keys');
    }
    setSaving(false);
  };

  const handleDelete = async (provider) => {
    setSaving(true);
    const result = await deleteWorkspaceAPIKey(workspaceId, provider);
    if (result.success) {
      toast.success(`${PROVIDERS[provider].name} API key deleted`);
      await loadAPIKeys();
    } else {
      toast.error(result.error || 'Failed to delete API key');
    }
    setSaving(false);
  };

  const hasChanges = newKeys.openai || newKeys.anthropic || newKeys.groq;
  const configuredCount = [
    apiKeysStatus.is_openai_available,
    apiKeysStatus.is_anthropic_available,
    apiKeysStatus.is_groq_available
  ].filter(Boolean).length;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 rounded-full animate-spin border-2 border-primary-500/20 border-t-primary-500" />
        </div>
        <p className="text-sm text-text-tertiary">Loading API keys...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Single Card containing everything */}
      <div className="rounded-xl overflow-hidden bg-surface-primary/95 border border-primary-500/15 shadow-lg">
        {/* Card Header */}
        <div className="px-5 py-4 flex items-center justify-between bg-gradient-to-r from-primary-500/8 to-transparent border-b border-border-secondary">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-success-50 border border-primary-500/30 text-primary-500">
              <KeyIcon />
            </div>
            <div>
              <h3 className="text-base font-semibold text-text-primary">
                LLM Providers
              </h3>
              <p className="text-xs text-text-tertiary">
                Configure API keys for AI model providers
              </p>
            </div>
          </div>
          
          {/* Status Badge */}
          <div className="flex items-center gap-3">
            {hasChanges && (
              <div className="flex items-center gap-2 mr-2">
                <span className="w-2 h-2 rounded-full animate-pulse bg-warning" />
                <span className="text-xs text-warning">Unsaved</span>
              </div>
            )}
            <div className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-surface-secondary/80 border border-primary-500/20">
              <div className={`w-2 h-2 rounded-full ${configuredCount > 0 ? 'bg-primary-500 shadow-glow-sm' : 'bg-gray-600'}`} />
              <span className="text-xs font-medium text-text-secondary">
                {configuredCount}/3
              </span>
            </div>
          </div>
        </div>

        {/* Provider Rows */}
        <ProviderRow
          provider="openai"
          value={newKeys.openai}
          maskedValue={apiKeysStatus.openai_api_key_masked}
          isAvailable={apiKeysStatus.is_openai_available}
          onChange={handleKeyChange}
          onDelete={handleDelete}
          isLoading={saving}
          isLast={false}
        />

        <ProviderRow
          provider="anthropic"
          value={newKeys.anthropic}
          maskedValue={apiKeysStatus.anthropic_api_key_masked}
          isAvailable={apiKeysStatus.is_anthropic_available}
          onChange={handleKeyChange}
          onDelete={handleDelete}
          isLoading={saving}
          isLast={false}
        />

        <ProviderRow
          provider="groq"
          value={newKeys.groq}
          maskedValue={apiKeysStatus.groq_api_key_masked}
          isAvailable={apiKeysStatus.is_groq_available}
          onChange={handleKeyChange}
          onDelete={handleDelete}
          isLoading={saving}
          isLast={true}
        />

        {/* Save Button Footer */}
        {hasChanges && (
          <div className="px-5 py-4 flex items-center justify-between bg-surface-secondary/80 border-t border-border-secondary">
            <div className="flex items-center gap-3">
              <span className="text-warning"><KeyIcon /></span>
              <div>
                <p className="text-sm font-medium text-text-primary">Ready to save</p>
                <p className="text-xs text-text-tertiary">
                  {[
                    newKeys.openai && 'OpenAI',
                    newKeys.anthropic && 'Anthropic', 
                    newKeys.groq && 'Groq'
                  ].filter(Boolean).join(', ')} will be updated
                </p>
              </div>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="py-2.5 px-5 text-sm font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-br from-primary-600 to-primary-500 text-black shadow-glow hover:shadow-glow-lg hover:-translate-y-0.5"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full border-2 animate-spin border-black/30 border-t-black" />
                  Saving...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <CheckIcon />
                  Save Changes
                </span>
              )}
            </button>
          </div>
        )}

        {/* Security Notice - Inside card footer */}
        <div className="flex items-center gap-3 px-5 py-3 bg-primary-500/3 border-t border-border-secondary">
          <span className="text-primary-500"><ShieldIcon /></span>
          <p className="text-xs text-text-tertiary">
            <span className="text-text-secondary font-medium">End-to-end encryption</span> — API keys are encrypted at rest using AES-256. Only workspace admins can access.
          </p>
        </div>
      </div>
    </div>
  );
};

APIKeysTab.propTypes = {
  workspaceId: PropTypes.string.isRequired
};

export default APIKeysTab;
